import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import MarkerShape from "@mapmycustomers/shared/enum/MarkerShape";
import MarkerSize from "@mapmycustomers/shared/enum/MarkerSize";
import { AnyEntityId, Company, Person } from "@mapmycustomers/shared/types/entity";
import { EntityPin } from "@mapmycustomers/shared/types/map";

import MapTooltip from "@app/component/map/core/chrome/MapTooltip";
import { Feature } from "@app/component/map/FeaturedMap/index";
import AlertBadgesType from "@app/enum/map/AlertBadgesType";
import MarkerEffect from "@app/enum/MarkerEffect";
import { getMapSettings } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { PIN_ZINDEX } from "@app/util/map/consts";
import defaultMapEntryGeometryGetter from "@app/util/map/defaultMapEntryGeometryGetter";
import { mapPinIdGetter } from "@app/util/map/idGetters";
import isValidMapEntry from "@app/util/map/isValidMapEntry";
import defaultEntityStyleGetter from "@app/util/map/markerStyles/defaultEntityStyleGetter";
import typedIdGetter from "@app/util/typedIdGetter";
import { getEntityTypeAnalyticsName } from "@app/util/ui";

const isDifferent = (a: EntityPin, b: EntityPin) =>
  a.id !== b.id ||
  a.data.entity !== b.data.entity ||
  a.data.color !== b.data.color ||
  a.data.shape !== b.data.shape ||
  a.sumAmount !== b.sumAmount ||
  a.data.outOfCadence !== b.data.outOfCadence ||
  a.data.overdueActivitiesCount !== b.data.overdueActivitiesCount ||
  a.data.cadenceDaysOut !== b.data.cadenceDaysOut ||
  (a.data as Company | Person)?.upcomingActivity?.startAt !==
    (b.data as Company | Person)?.upcomingActivity?.startAt ||
  Math.abs(a.region!.latitude - b.region!.latitude) > Number.EPSILON ||
  Math.abs(a.region!.longitude - b.region!.longitude) > Number.EPSILON;

interface Props {
  annotatedPinId?: string;
  areBadgesVisible?: boolean;
  entityPins: EntityPin[];
  focusedEntityId?: AnyEntityId;
  mapSettings: {
    markerSize: MarkerSize;
    showEntityIcons: boolean;
  };
  onClick?: (entity: EntityPin) => void;
  onDoubleClick?: (entity: EntityPin) => void;
  styleGetter?: (item: EntityPin) => google.maps.Data.StyleOptions;
  type?: string;
  visibleBadges?: AlertBadgesType[];
  zIndex?: number;
}

const EntityFeature = ({
  annotatedPinId,
  areBadgesVisible = false,
  entityPins,
  focusedEntityId,
  mapSettings,
  onClick,
  onDoubleClick,
  styleGetter,
  type = "entities",
  visibleBadges,
  zIndex = PIN_ZINDEX,
}: Props) => {
  const analytics = useAnalytics();

  const [hoveredPinId, setHoveredPinId] = useState<string | undefined>(undefined);

  const handleEntityClick = useCallback(
    (entity: EntityPin, event: google.maps.MapMouseEvent) => {
      event.stop();
      onClick?.(entity);
      analytics.clicked(["Pin"], {
        entityType: getEntityTypeAnalyticsName(entity.data.entity, false, true),
      });
    },
    [analytics, onClick]
  );

  const handleEntityDoubleClick = useCallback(
    (entity: EntityPin) => {
      onDoubleClick?.(entity);
      analytics.clicked(["Pin"], {
        doubleClick: true,
        entityType: getEntityTypeAnalyticsName(entity.data.entity, false, true),
      });
    },
    [analytics, onDoubleClick]
  );

  const handleEntityMouseOver = useCallback(
    (pin: EntityPin) => setHoveredPinId(mapPinIdGetter(pin)),
    []
  );
  const handleEntityMouseLeave = useCallback(() => setHoveredPinId(undefined), []);

  const hoveredPin: EntityPin | undefined = useMemo(
    () =>
      hoveredPinId
        ? entityPins.find((pin) => {
            const pinId = mapPinIdGetter(pin);
            return pinId === hoveredPinId && pinId !== annotatedPinId;
          })
        : undefined,
    [annotatedPinId, entityPins, hoveredPinId]
  );
  const hoveredPinPosition = useMemo(
    () =>
      hoveredPin?.region
        ? { lat: hoveredPin.region.latitude, lng: hoveredPin.region.longitude }
        : undefined,
    [hoveredPin]
  );

  const entityEffectGetter = useMemo(
    () => (entityPin: EntityPin) =>
      annotatedPinId === mapPinIdGetter(entityPin) ? MarkerEffect.OUTLINE : MarkerEffect.SHADOW,
    [annotatedPinId]
  );

  const entityIconGetter = useMemo(
    () => (entityPin: EntityPin) => {
      const icon = mapSettings.showEntityIcons ? entityPin.data.entity : undefined;
      return focusedEntityId === entityPin.data.id ? "selected" : icon;
    },
    [focusedEntityId, mapSettings.showEntityIcons]
  );

  const entityPinStyleGetter = useMemo(() => {
    return defaultEntityStyleGetter({
      effect: entityEffectGetter,
      icon: entityIconGetter,
      markerSize: mapSettings.markerSize,
      shape: (entityPin: EntityPin) => entityPin.data.shape ?? MarkerShape.SQUARE,
      showEntityIcon: mapSettings.showEntityIcons,
      zIndex,
      ...(visibleBadges?.length ? { areBadgesVisible, visibleBadges } : {}),
    });
  }, [areBadgesVisible, entityEffectGetter, entityIconGetter, mapSettings, visibleBadges, zIndex]);

  return (
    <>
      <Feature<EntityPin>
        geometryGetter={defaultMapEntryGeometryGetter}
        idGetter={typedIdGetter}
        isDifferent={isDifferent}
        items={entityPins.filter(isValidMapEntry)}
        onClick={handleEntityClick}
        onDoubleClick={handleEntityDoubleClick}
        onMouseLeave={handleEntityMouseLeave}
        onMouseOver={handleEntityMouseOver}
        styleGetter={styleGetter ?? entityPinStyleGetter}
        type={type}
      />
      {hoveredPin && <MapTooltip position={hoveredPinPosition} tooltip={hoveredPin.data.name} />}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  mapSettings: getMapSettings(state),
});

export default connect(mapStateToProps)(EntityFeature);
